import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Cloud,
  DollarSign,
  Gauge,
  RefreshCcw,
  Route,
  XCircle,
} from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { EmptyReport, ReportCard, ReportPanel } from "./components/ReportCards";
import { dt, n, td, th } from "./components/reportFormat";

const EVENT_PAGE_SIZE = 20;
const tableWrap = "overflow-hidden rounded-xl border border-slate-100";
const tableScroll = "overflow-x-auto";
const rowClass = "border-t border-slate-100 transition hover:bg-slate-50/70";
const compactTh = `${th} whitespace-nowrap bg-slate-50/80 py-2.5 text-[10px] uppercase tracking-wider text-slate-500`;
const compactTd = `${td} py-2.5 align-middle`;

function num(value) {
  const x = Number(value || 0);
  return Number.isFinite(x) ? x : 0;
}

function usd(value) {
  return `$${num(value).toFixed(4)}`;
}

function capText(row) {
  if (row?.unlimitedFree) return "Unlimited";
  if (row?.freeUsageCap === null || row?.freeUsageCap === undefined) return "—";
  return n(row.freeUsageCap);
}

function remainingText(row) {
  if (row?.unlimitedFree) return "Unlimited";
  if (row?.freeRemaining === null || row?.freeRemaining === undefined) return "—";
  return n(row.freeRemaining);
}

function indiaMonth(offset = 0) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const year = Number(parts.find((part) => part.type === "year")?.value || new Date().getFullYear());
  const month = Number(parts.find((part) => part.type === "month")?.value || (new Date().getMonth() + 1));
  const date = new Date(Date.UTC(year, month - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(value) {
  if (!/^\d{4}-\d{2}$/.test(String(value || ""))) return "Selected Month";
  const [year, month] = String(value).split("-").map(Number);
  return new Intl.DateTimeFormat("en-IN", { month: "long", year: "numeric" }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function cleanLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  return text || fallback;
}

const PURPOSE_LABELS = Object.freeze({
  ORDER_TRACKING: "Customer Order Tracking Route",
  GROCERY_TRACK_ORDER_MAP: "Grocery Order Tracking Map",
  FOOD_TRACK_ORDER_MAP: "Food Order Tracking Map",
  MANAGE_ADDRESS_MAP: "Manage Address Map",
  MANAGE_ADDRESS_AUTOCOMPLETE: "Manage Address Search",
  MANAGE_ADDRESS_REVERSE_GEOCODE: "Manage Address Location",
  MANAGE_ADDRESS_TEXT_GEOCODE: "Manage Address Search",
  MANAGE_ADDRESS_PLACE_ID_GEOCODE: "Manage Address Selected Place",
  ONBOARDING_ADD_ADDRESS_REVERSE_GEOCODE: "Onboarding Address Location",
  ONBOARDING_ENABLE_LOCATION_REVERSE_GEOCODE: "Onboarding Current Location",
  STARTUP_LOCATION_REVERSE_GEOCODE: "App Startup Location",
  HOME_LOCATION_REVERSE_GEOCODE: "Home Location",
  GROCERY_CART_RAIN_CHARGE: "Grocery Rain Charge",
  FOOD_CART_RAIN_CHARGE: "Food Cart Rain Charge",
  FOOD_ORDER_RAIN_CHARGE: "Food Order Rain Charge",
  WAREHOUSE_ORDER_TRACKING_DIRECTIONS: "Admin Order Tracking Route",
  WAREHOUSE_ORDER_TRACKING_MAP: "Admin Order Tracking Map",
  REVERSE_GEOCODE: "Location Address Lookup",
  ADDRESS_GEOCODE: "Address Search",
  PLACE_ID_GEOCODE: "Selected Address Lookup",
  PLACE_AUTOCOMPLETE: "Address Search Suggestions",
  MOBILE_LOCATION: "Mobile Location",
});

const API_FALLBACK_USES = Object.freeze({
  ROUTES: ["Customer Order Tracking Route"],
  WEATHER: ["Grocery / Food Rain Charge"],
  GEOCODING: ["Onboarding & Address Location"],
  PLACES: ["Manage Address Search"],
  MAPS_SDK: ["Manage Address & Order Tracking Maps"],
  MAPS_JAVASCRIPT: ["Admin Order Tracking Map"],
  DIRECTIONS: ["Admin Order Tracking Route"],
});

function featureLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Other";
  if (PURPOSE_LABELS[raw]) return PURPOSE_LABELS[raw];
  return raw
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function usedForLabels(row) {
  const api = String(row?.apiFamily || row?.api_family || "").trim().toUpperCase();
  const purposes = Array.isArray(row?.purposes) ? row.purposes : [];
  const labels = purposes.map(featureLabel).filter(Boolean);
  const unique = [...new Set(labels)];
  return unique.length ? unique : (API_FALLBACK_USES[api] || ["Other Google Feature"]);
}

function UsedForCell({ row }) {
  const labels = usedForLabels(row);
  const visible = labels.slice(0, 3);
  const hiddenCount = Math.max(0, labels.length - visible.length);
  return (
    <div className="flex max-w-[320px] flex-wrap gap-1.5">
      {visible.map((label) => (
        <span key={label} className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">
          {label}
        </span>
      ))}
      {hiddenCount ? (
        <span className="rounded-full bg-slate-50 px-2 py-1 text-[10px] font-black text-slate-400" title={labels.slice(3).join(", ")}>
          +{hiddenCount}
        </span>
      ) : null}
    </div>
  );
}

function shortId(value) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= 16) return text;
  return `${text.slice(0, 8)}…${text.slice(-5)}`;
}

function StatusBadge({ success, httpStatus }) {
  if (success) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-black text-emerald-700">
        <CheckCircle2 size={12} /> OK{httpStatus ? ` · ${httpStatus}` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2 py-1 text-[10px] font-black text-rose-700">
      <XCircle size={12} /> Failed{httpStatus ? ` · ${httpStatus}` : ""}
    </span>
  );
}

export default function GoogleUsageReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usageMonth, setUsageMonth] = useState(() => indiaMonth(0));

  const [eventPage, setEventPage] = useState(1);
  const [eventData, setEventData] = useState({ rows: [], total: 0, page: 1, limit: EVENT_PAGE_SIZE });
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await ReportsService.googleUsageSummary({ usageMonth });
      setData(response?.data?.data || response?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Unable to load Google API usage");
    } finally {
      setLoading(false);
    }
  }, [usageMonth]);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError("");
    try {
      const response = await ReportsService.googleUsageEvents({
        usageMonth,
        page: eventPage,
        limit: EVENT_PAGE_SIZE,
      });
      const payload = response?.data?.data || response?.data || {};
      setEventData({
        rows: Array.isArray(payload.rows) ? payload.rows : [],
        total: num(payload.total),
        page: num(payload.page) || eventPage,
        limit: num(payload.limit) || EVENT_PAGE_SIZE,
      });
    } catch (e) {
      setEventsError(e?.response?.data?.message || e?.message || "Unable to load Google API attempts");
    } finally {
      setEventsLoading(false);
    }
  }, [usageMonth, eventPage]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const changeMonth = useCallback((month) => {
    setEventPage(1);
    setUsageMonth(month || indiaMonth(0));
  }, []);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(num(eventData.total) / Math.max(1, num(eventData.limit) || EVENT_PAGE_SIZE))),
    [eventData.total, eventData.limit]
  );

  useEffect(() => {
    if (eventPage > totalPages) setEventPage(totalPages);
  }, [eventPage, totalPages]);

  if (loading && !data) {
    return <div className="p-8 text-center text-sm font-bold text-[#d86a78]">Loading Google API usage…</div>;
  }

  const monthly = data?.monthlyBilling || {};

  return (
    <div className="space-y-5">
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">{error}</div> : null}

      <div className="flex flex-col gap-3 rounded-xl border border-[#f4cdd2] bg-[#fffafb] p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-black text-slate-700">
          <CalendarDays size={16} className="text-[#d86a78]" />
          {monthLabel(monthly.month || usageMonth)}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => changeMonth(indiaMonth(-1))} className="rounded-lg border border-[#f2c6cc] bg-white px-3 py-2 text-xs font-black text-[#d86a78] hover:bg-[#fff8f9]">Last Month</button>
          <button type="button" onClick={() => changeMonth(indiaMonth(0))} className="rounded-lg border border-[#f2c6cc] bg-white px-3 py-2 text-xs font-black text-[#d86a78] hover:bg-[#fff8f9]">Current Month</button>
          <input
            aria-label="Google usage month"
            type="month"
            value={usageMonth}
            max={indiaMonth(0)}
            onChange={(event) => changeMonth(event.target.value || indiaMonth(0))}
            className="rounded-lg border border-[#f2c6cc] bg-white px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-[#d86a78]"
          />
          <button type="button" onClick={() => { loadSummary(); loadEvents(); }} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff8f9]" title="Refresh">
            <RefreshCcw size={14} />
          </button>
        </div>
      </div>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={Cloud} title="Total Calls" value={n(monthly.totalCalls)} />
        <ReportCard icon={Route} title="Usage Units" value={n(monthly.totalUnits)} />
        <ReportCard icon={DollarSign} title="Estimated Cost" value={usd(monthly.estimatedCostUsd)} />
        <ReportCard icon={Gauge} title="Paid Units" value={n(monthly.paidUnits)} />
      </div>

      <ReportPanel title="Google API Usage">
        {(monthly?.byApi || []).length ? (
          <div className={tableWrap}>
            <div className={tableScroll}>
              <table className="min-w-[1180px] w-full">
                <thead>
                  <tr>
                    <th className={compactTh}>API</th>
                    <th className={compactTh}>Used For</th>
                    <th className={compactTh}>Calls</th>
                    <th className={compactTh}>Success</th>
                    <th className={compactTh}>Failed</th>
                    <th className={compactTh}>Units</th>
                    <th className={compactTh}>Free Cap</th>
                    <th className={compactTh}>Free Left</th>
                    <th className={compactTh}>Paid</th>
                    <th className={compactTh}>Rate / 1K</th>
                    <th className={compactTh}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.byApi.map((row) => (
                    <tr key={row.apiFamily || row.api_family} className={rowClass}>
                      <td className={compactTd}>
                        <div className="font-black text-slate-800">{row.apiFamily || row.api_family}</div>
                        <div className="mt-0.5 max-w-[240px] truncate text-[10px] font-semibold text-slate-400" title={row.skuName || ""}>{row.skuName || "—"}</div>
                      </td>
                      <td className={compactTd}><UsedForCell row={row} /></td>
                      <td className={compactTd}>{n(row.attempts)}</td>
                      <td className={`${compactTd} text-emerald-600`}>{n(row.successful)}</td>
                      <td className={`${compactTd} ${num(row.failed) ? "text-rose-600" : "text-slate-400"}`}>{n(row.failed)}</td>
                      <td className={compactTd}>{n(row.units)}</td>
                      <td className={compactTd}>{capText(row)}</td>
                      <td className={compactTd}>{remainingText(row)}</td>
                      <td className={compactTd}>{row.paidUnits === null || row.paidUnits === undefined ? "—" : n(row.paidUnits)}</td>
                      <td className={compactTd}>{row.pricePer1000Usd === null || row.pricePer1000Usd === undefined ? "—" : usd(row.pricePer1000Usd)}</td>
                      <td className={`${compactTd} font-black text-slate-800`}>{row.estimatedCostUsd === null || row.estimatedCostUsd === undefined ? "—" : usd(row.estimatedCostUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : <EmptyReport text="No Google API usage for this month" />}
      </ReportPanel>

      <div className="grid gap-5 xl:grid-cols-2">
        <ReportPanel title="Usage by Feature">
          {(data?.byPurpose || []).length ? (
            <div className={tableWrap}>
              <div className="max-h-[360px] overflow-auto">
                <table className="w-full min-w-[420px]">
                  <thead className="sticky top-0 z-10"><tr><th className={compactTh}>Feature</th><th className={compactTh}>Calls</th><th className={compactTh}>Units</th></tr></thead>
                  <tbody>{data.byPurpose.map((row) => <tr key={row.purpose} className={rowClass}><td className={`${compactTd} font-bold text-slate-700`}>{featureLabel(row.purpose)}</td><td className={compactTd}>{n(row.attempts)}</td><td className={compactTd}>{n(row.billable_units)}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          ) : <EmptyReport />}
        </ReportPanel>

        <ReportPanel title="Usage by Credential">
          {(data?.byCredential || []).length ? (
            <div className={tableWrap}>
              <div className="max-h-[360px] overflow-auto">
                <table className="w-full min-w-[560px]">
                  <thead className="sticky top-0 z-10"><tr><th className={compactTh}>Credential</th><th className={compactTh}>API</th><th className={compactTh}>Calls</th><th className={compactTh}>Units</th><th className={compactTh}>Failed</th></tr></thead>
                  <tbody>{data.byCredential.map((row, idx) => <tr key={`${row.credential_alias}-${row.api_family}-${idx}`} className={rowClass}><td className={`${compactTd} max-w-[220px] truncate font-bold text-slate-700`} title={row.credential_alias}>{cleanLabel(row.credential_alias)}</td><td className={compactTd}>{row.api_family}</td><td className={compactTd}>{n(row.attempts)}</td><td className={compactTd}>{n(row.billable_units)}</td><td className={`${compactTd} ${num(row.failed) ? "text-rose-600" : "text-slate-400"}`}>{n(row.failed)}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          ) : <EmptyReport />}
        </ReportPanel>
      </div>

      <ReportPanel
        title="Recent Google API Attempts"
        right={eventData.total > 0 ? <span className="text-[11px] font-bold text-slate-400">{n(eventData.total)} total</span> : null}
      >
        {eventsError ? <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700">{eventsError}</div> : null}
        {eventsLoading && !eventData.rows.length ? (
          <div className="py-8 text-center text-xs font-bold text-slate-400">Loading attempts…</div>
        ) : eventData.rows.length ? (
          <>
            <div className={tableWrap}>
              <div className={tableScroll}>
                <table className="w-full min-w-[980px]">
                  <thead>
                    <tr>
                      <th className={compactTh}>Time</th>
                      <th className={compactTh}>API</th>
                      <th className={compactTh}>Feature</th>
                      <th className={compactTh}>Source</th>
                      <th className={compactTh}>User / Order</th>
                      <th className={compactTh}>Result</th>
                      <th className={compactTh}>Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventData.rows.map((row) => (
                      <tr key={row.event_id} className={rowClass}>
                        <td className={`${compactTd} whitespace-nowrap text-slate-500`}>{dt(row.occurred_at)}</td>
                        <td className={compactTd}>
                          <div className="font-black text-slate-800">{cleanLabel(row.api_family)}</div>
                          <div className="mt-0.5 max-w-[180px] truncate text-[10px] font-semibold text-slate-400" title={row.api_method}>{cleanLabel(row.api_method)}</div>
                        </td>
                        <td className={`${compactTd} max-w-[220px] truncate font-bold text-slate-700`} title={featureLabel(row.purpose)}>{featureLabel(row.purpose)}</td>
                        <td className={compactTd}>
                          <div className="font-bold text-slate-700">{cleanLabel(row.service)}</div>
                          <div className="mt-0.5 text-[10px] font-semibold text-slate-400">{cleanLabel(row.source_type, "")}</div>
                        </td>
                        <td className={compactTd}>
                          <div className="font-mono text-[10px] font-bold text-slate-600" title={row.user_id || ""}>{shortId(row.user_id)}</div>
                          {row.order_id ? <div className="mt-0.5 font-mono text-[10px] text-slate-400" title={row.order_id}>{shortId(row.order_id)}</div> : null}
                        </td>
                        <td className={compactTd}><StatusBadge success={row.success} httpStatus={row.http_status} /></td>
                        <td className={`${compactTd} whitespace-nowrap`}>{row.latency_ms === null || row.latency_ms === undefined ? "—" : `${n(row.latency_ms)} ms`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-[11px] font-bold text-slate-400">
                Page {eventPage} of {totalPages} · {n(eventData.total)} attempts
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEventPage((page) => Math.max(1, page - 1))}
                  disabled={eventPage <= 1 || eventsLoading}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <button
                  type="button"
                  onClick={() => setEventPage((page) => Math.min(totalPages, page + 1))}
                  disabled={eventPage >= totalPages || eventsLoading}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        ) : <EmptyReport text="No Google API attempts for this month" />}
      </ReportPanel>
    </div>
  );
}
